// Evidence uploads (P2.4a). Both are records, never actions on someone's behalf:
//  - RecordClientAcceptanceForm records the CLIENT's acceptance of an issued
//    quote (ruling D): their evidence file, the client's person who accepted and
//    the date they accepted. The coordinator's click alone is never acceptance,
//    so nothing here is labelled "Accept".
//  - FundingEvidenceForm records payment-received evidence or a purchase order.
//    The booking ledger never moves money: this is never a collection.
// Files: PDF, PNG or JPEG up to 10 MB (commerce checks the bytes).
import React, { useState } from 'react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import {
  type Booking, type EvidenceKind, type FundingKind, type Quote, recordClientAcceptance, recordFundingEvidence,
} from '@/services/baBookingService';
import { zonedLocalToIso } from './bookingFormat';
import { Field, type K, NativeSelect, useRunAction } from './shared';

const ACCEPT = 'application/pdf,image/png,image/jpeg';
const MAX = 10 * 1024 * 1024;

export const RecordClientAcceptanceForm: React.FC<{
  quote: Quote; booking: Booking; k: K; onDone: (q: Quote) => void;
}> = ({ quote, booking, k, onDone }) => {
  const { busy, run } = useRunAction(k);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [kind, setKind] = useState<EvidenceKind | ''>('');
  const tooBig = !!file && file.size > MAX;
  const today = new Date().toISOString().slice(0, 10);
  const ready = !!file && !tooBig && name.trim().length > 0 && !!date && date <= today && !!kind;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || !file || !kind) return;
    const out = await run((key) => recordClientAcceptance(quote.id, {
      file, clientPersonName: name, clientAcceptedAt: zonedLocalToIso(`${date}T00:00`, booking.timezone), evidenceKind: kind,
    }, { version: quote.version, key }), k('acceptance.done', 'The client’s acceptance is recorded.'));
    if (out) onDone(out);
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-border p-3" data-testid="record-acceptance">
      <div>
        <h4 className="text-sm font-semibold text-foreground">{k('acceptance.title', 'Record client acceptance')}</h4>
        <p className="text-xs text-muted-foreground">
          {k('acceptance.help', 'Record that the client accepted quote version {{n}}. Attach their evidence: the signed quote, their email or their written message. Your record is not the client’s acceptance; their evidence is.', { n: quote.versionNo })}
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={k('acceptance.person', 'Client’s person who accepted')} htmlFor={`acc-name-${quote.id}`}>
          <Input id={`acc-name-${quote.id}`} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label={k('acceptance.date', 'Date the client accepted')} htmlFor={`acc-date-${quote.id}`}>
          <Input id={`acc-date-${quote.id}`} type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} required />
        </Field>
        <Field label={k('acceptance.kind', 'Evidence type')} htmlFor={`acc-kind-${quote.id}`}>
          <NativeSelect id={`acc-kind-${quote.id}`} value={kind} onChange={(e) => setKind(e.target.value as EvidenceKind)} required>
            <option value="">{k('common.choose', 'Choose…')}</option>
            <option value="signed_quote">{k('acceptance.kind_signed_quote', 'Signed quote')}</option>
            <option value="email">{k('acceptance.kind_email', 'Email from the client')}</option>
            <option value="written_message">{k('acceptance.kind_written_message', 'Written message from the client')}</option>
          </NativeSelect>
        </Field>
        <Field label={k('acceptance.file', 'Client’s evidence (PDF, PNG or JPEG, up to 10 MB)')} htmlFor={`acc-file-${quote.id}`}>
          <Input id={`acc-file-${quote.id}`} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
        </Field>
      </div>
      {tooBig && <p className="text-sm text-danger-strong">{k('evidence.too_big', 'Evidence files can be at most 10 MB.')}</p>}
      <Button type="submit" disabled={busy || !ready}>{k('acceptance.submit', 'Record client acceptance')}</Button>
    </form>
  );
};

export const FundingEvidenceForm: React.FC<{ booking: Booking; k: K; onDone: (b: Booking) => void }> = ({ booking, k, onDone }) => {
  const { busy, run } = useRunAction(k);
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<FundingKind | ''>('');
  const [note, setNote] = useState('');
  const tooBig = !!file && file.size > MAX;
  const ready = !!file && !tooBig && !!kind && note.length <= 500;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || !file || !kind) return;
    const out = await run((key) => recordFundingEvidence(booking.id, { file, kind, note }, { version: booking.version, key }),
      k('funding.done', 'Funding evidence recorded.'));
    if (out) { onDone(out); setFile(null); setKind(''); setNote(''); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="funding-form">
      <p className="text-xs text-muted-foreground">
        {k('funding.help', 'Record evidence that the client paid, or their purchase order. This is a record only: KaziPeople never collects or moves the money. There is no credit exception in the pilot.')}
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={k('funding.kind', 'Evidence type')} htmlFor={`fund-kind-${booking.id}`}>
          <NativeSelect id={`fund-kind-${booking.id}`} value={kind} onChange={(e) => setKind(e.target.value as FundingKind)} required>
            <option value="">{k('common.choose', 'Choose…')}</option>
            <option value="payment_received_evidence">{k('funding.kind_payment', 'Payment received (evidence)')}</option>
            <option value="purchase_order">{k('funding.kind_po', 'Purchase order')}</option>
          </NativeSelect>
        </Field>
        <Field label={k('funding.file', 'Evidence file (PDF, PNG or JPEG, up to 10 MB)')} htmlFor={`fund-file-${booking.id}`}>
          <Input id={`fund-file-${booking.id}`} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
        </Field>
        <Field label={k('funding.note', 'Note (optional)')} htmlFor={`fund-note-${booking.id}`} className="md:col-span-2">
          <Input id={`fund-note-${booking.id}`} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {tooBig && <p className="text-sm text-danger-strong">{k('evidence.too_big', 'Evidence files can be at most 10 MB.')}</p>}
      <Button type="submit" disabled={busy || !ready}>{k('funding.submit', 'Record funding evidence')}</Button>
    </form>
  );
};
